import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { concat, forkJoin, Observable, Subject } from 'rxjs';
import { last, catchError, map, tap } from 'rxjs/operators';
import { instanceToPlain, plainToInstance } from 'class-transformer';
import { Cacheable } from 'ts-cacheable';
import { OrderApiModel } from '../model/api-model/order/order.api.model';
import { OrderAliasApiModel } from '../model/api-model/order/order-alias.api.model';
import { CreateNewOrderRequest } from '../model/request/create-new-order-request.model';
import { OrderMembershipUpdateRequestModel } from '../model/request/order-membership-update.request.model';
import { PaymentConfigApiModel } from '../model/api-model/payment/config/payment.config.api.model';
import { PaymentProviderPayMethodRequestModel } from '../model/request/payment-provider-pay-method.request.model';
import { VirtualPassApiModel } from '../model/api-model/sales-document/virtual-pass/virtual-pass.api.model';
import { CateringApiModel } from '../model/api-model/order/catering.api.model';
import { PickupTimeApiModel } from '../model/api-model/pickup-time.api.model';
import { ENVIRONMENT_TOKEN } from '../injection.tokens';
import { PaymentApiModel } from '../model/api-model/payment.api.model';
import { PaymentMethodApiModel } from '../model/api-model/order/payment-method/payment-method.api.model';
import { PaymentProviderPayMethodApiModel } from '../model/api-model/payment-provider-pay-method.response.model';
import { NutritionalInfoModel } from '../model/catering/nutritional-info.response.model';
import { TableApiModel } from '../model/api-model/table/table.api.model';
import { SeatRequestRequestModel } from '../model/api-model/seat/seat.request.model';
import { CateringResponseModel } from '../model/catering/catering.response.model';
import { DateTime } from 'luxon';
import { ExecutionStrategy } from '../enum/execution-strategy.enum';
import { CreatePaymentRequestModel } from '../model/request/create-payment.request.model';
import { getStringValueOrNull } from '../function/custom-function';
import { IOrderPaymentPackage } from '../model/interface/order-payment.package.model';
import { InvoiceDataRequestModel } from '../model/api-model/invoice-data.request.model';
import { RecalculateOperationType } from '../enum/recalculate-operation-type.enum';
import { ErrorHandlerService } from '../service/error-handler/error-handler.service';
import { TransactionNumberByVoucherNumber } from '../model/interface/transaction-number-by-voucher-number.interface';
import { OrderTicketHttpService } from './order-ticket.http.service';

@Injectable({
  providedIn: 'root'
})
export class OrderHttpService {
  public static cacheBuster$ = new Subject<void>();
  public static cacheModifier$ = new Subject<any>();

  public static paymentProviderCacheBuster$ = new Subject<void>();

  constructor(
    @Inject(ENVIRONMENT_TOKEN) protected environment: any,
    private http: HttpClient,
    private errorHandlerService: ErrorHandlerService
  ) {}

  public getOrder(cinemaId: string, orderId: string, executionStrategy: ExecutionStrategy): Observable<OrderApiModel> {
    if (executionStrategy === ExecutionStrategy.FORCE) {
      OrderHttpService.cacheBuster$.next();
    }

    return this.http.get<OrderApiModel>(`/cinema/${cinemaId}/order/${orderId}`);
  }

  public getOrderTransactionNumber(bookingId: string): Observable<OrderAliasApiModel> {
    return this.http.get<OrderAliasApiModel>(`/order/transactionNumber/${bookingId}`);
  }

  public create(cinemaId: string, sourceOrderId: string = null): Observable<OrderApiModel> {
    const createNewOrderRequest: CreateNewOrderRequest = {
      sourceOrderId: sourceOrderId,
      autoAssignClientMemberCard: true
    };

    return this.http.post<OrderApiModel>(`/cinema/${cinemaId}/order`, instanceToPlain(createNewOrderRequest)).pipe(
      tap(() => OrderHttpService.cacheBuster$.next()),
      map((res) => {
        return plainToInstance(OrderApiModel, res as Object, { strategy: 'excludeAll' });
      }),
      catchError((error) => {
        if (this.errorHandlerService.getCode(error) != '217') {
          throw error;
        }

        const fallbackRequest = {
          ...createNewOrderRequest,
          autoAssignClientMemberCard: false
        };

        return this.http.post<OrderApiModel>(`/cinema/${cinemaId}/order`, instanceToPlain(fallbackRequest)).pipe(
          map((res) => plainToInstance(OrderApiModel, res as Object, { strategy: 'excludeAll' })),
          tap(() => OrderHttpService.cacheBuster())
        );
      })
    );
  }

  public update(cinemaId: string, order: OrderApiModel, autoAssignClientMemberCard: boolean = false) {
    return this.http
      .put<OrderApiModel>(`/cinema/${cinemaId}/order/${order.id}`, { ...instanceToPlain(order), autoAssignClientMemberCard: autoAssignClientMemberCard })
      .pipe(
        catchError((error) => {
          if (this.errorHandlerService.getCode(error) != '217') {
            throw error;
          }

          return this.http.put<OrderApiModel>(`/cinema/${cinemaId}/order/${order.id}`, { ...instanceToPlain(order), autoAssignClientMemberCard: false });
        }),
        tap((res) => OrderHttpService.cacheModify(res.id, res))
      );
  }

  silentDelete(cinemaId: string, orderId: string, token: string | null = null): boolean {
    let apiUrl: string = this.environment.apiUrl;

    if (apiUrl.endsWith('/') === false) {
      apiUrl = apiUrl + '/';
    }

    const endpoint = `cinema/${cinemaId}/order/${orderId}/delete`;

    return navigator.sendBeacon(apiUrl + endpoint);
  }

  public updateAgreement(cinemaId: string, orderId: string, agreements: string[]) {
    return this.http.put(`/cinema/${cinemaId}/order/${orderId}/agreements`, agreements);
  }

  public delete(cinemaId: string, orderId: string) {
    return this.http.delete(`/cinema/${cinemaId}/order/${orderId}`).pipe(tap(() => OrderHttpService.cacheBuster()));
  }

  public deleteItem(cinemaId: string, orderId: string, id: string) {
    return this.http.delete(`/cinema/${cinemaId}/order/${orderId}/item/${id}`).pipe(tap(() => OrderHttpService.cacheBuster()));
  }

  public deleteItemsSequentially(cinemaId: string, orderId: string, itemIds: string[]): Observable<any> {
    const requests = itemIds.map((itemId) => this.http.delete(`/cinema/${cinemaId}/order/${orderId}/item/${itemId}`));

    return concat(...requests).pipe(
      last(),
      tap(() => OrderHttpService.cacheBuster())
    );
  }

  public deleteItemsConcurrently(cinemaId: string, orderId: string, itemIds: string[]): Observable<any> {
    const requests = itemIds.map((itemId) => this.http.delete(`/cinema/${cinemaId}/order/${orderId}/item/${itemId}`));

    return forkJoin(requests).pipe(
      map((responses) => responses[responses.length - 1]),
      tap(() => OrderHttpService.cacheBuster())
    );
  }

  public closeBasket(cinemaId: string, orderId: string, paymentConfirmed: boolean) {
    return this.http.post(`/cinema/${cinemaId}/order/${orderId}/close`, { paymentConfirmed: paymentConfirmed }).pipe(tap(() => OrderHttpService.cacheBuster()));
  }

  public membershipUpdate(cinemaId: string, orderId: string, request: OrderMembershipUpdateRequestModel) {
    return this.http.patch(`/cinema/${cinemaId}/order/${orderId}/membershipUpdate`, instanceToPlain(request)).pipe(tap(() => OrderHttpService.cacheBuster()));
  }

  public putPromotionToOrder(cinemaId: string, orderId: string, promotionId: string): Observable<OrderApiModel> {
    return this.http
      .put<OrderApiModel>(`/cinema/${cinemaId}/order/${orderId}/promotion/${promotionId}`, null)
      .pipe(tap((res) => OrderHttpService.cacheModify(res.id, res)));
  }

  public deletePromotionFromOrder(cinemaId: string, orderId: string, promotionId: string): Observable<OrderApiModel> {
    return this.http
      .delete<OrderApiModel>(`/cinema/${cinemaId}/order/${orderId}/promotion/${promotionId}`)
      .pipe(tap((res) => OrderHttpService.cacheModify(res.id, res)));
  }

  public postPayment(orderPaymentPackage: IOrderPaymentPackage): Observable<PaymentApiModel> {
    if (!orderPaymentPackage?.requestModel) {
      throw new Error('No request model');
    }

    const body = {
      channel: getStringValueOrNull(orderPaymentPackage.requestModel.channel, false),
      continueUrl: getStringValueOrNull(orderPaymentPackage.requestModel.continueUrl, false),
      intPayMethodType: getStringValueOrNull(orderPaymentPackage.requestModel.intPayMethodType, false),
      intPayMethodValue: getStringValueOrNull(orderPaymentPackage.requestModel.intPayMethodValue, false),
      saveToken: orderPaymentPackage.requestModel.saveToken,
      paymentToken: getStringValueOrNull(orderPaymentPackage.requestModel.paymentToken, false),
      expiry: getStringValueOrNull(orderPaymentPackage.requestModel.expiry, false),
      regulationAccept: orderPaymentPackage.requestModel.regulationAccept ?? false,
      clientAddress: orderPaymentPackage.requestModel.clientAddress
    } as CreatePaymentRequestModel;

    return this.http.post<PaymentApiModel>(
      `/cinema/${orderPaymentPackage.cinemaId}/order/${orderPaymentPackage.orderId}/payment/${orderPaymentPackage.paymentProviderIdentifier}`,
      body,
      {
        headers: orderPaymentPackage.recaptchaResponse ? { 'X-Recaptcha-Response': orderPaymentPackage.recaptchaResponse } : {}
      }
    );
  }

  public getPaymentMethodList(cinemaId: string, orderId: string) {
    return this.http
      .get<Array<PaymentMethodApiModel>>(`/cinema/${cinemaId}/order/${orderId}/payment/method`)
      .pipe(map((res) => plainToInstance(PaymentMethodApiModel, res as Object[], { strategy: 'excludeAll' })));
  }

  public getVerify(cinemaId: string, orderId: string) {
    return this.http.get(`/cinema/${cinemaId}/order/${orderId}/verify`);
  }

  public getPaymentProviderConfig(orderPaymentPackage: IOrderPaymentPackage): Observable<PaymentConfigApiModel> {
    const options: any = {
      params: {}
    };

    if (orderPaymentPackage.requestModel.channel !== null) {
      options.params.channel = orderPaymentPackage.requestModel.channel;
    }

    return this.http
      .get<PaymentConfigApiModel>(
        `/cinema/${orderPaymentPackage.cinemaId}/order/${orderPaymentPackage.orderId}/payment/${orderPaymentPackage.paymentProviderIdentifier}/config`,
        options
      )
      .pipe(map((res) => plainToInstance(PaymentConfigApiModel, res as Object, { strategy: 'excludeAll' })));
  }

  @Cacheable({ maxAge: 3600000, cacheBusterObserver: OrderHttpService.paymentProviderCacheBuster$ })
  public getPaymentProviderPayMethodCollection(orderPaymentPackage: IOrderPaymentPackage): Observable<Array<PaymentProviderPayMethodApiModel>> {
    return this.http
      .get<Array<PaymentProviderPayMethodApiModel>>(
        `/cinema/${orderPaymentPackage.cinemaId}/order/${orderPaymentPackage.orderId}/payment/${orderPaymentPackage.paymentProviderIdentifier}/methods`,
        {
          params: {
            channel: orderPaymentPackage.requestModel?.channel
          }
        }
      )
      .pipe(map((res) => plainToInstance(PaymentProviderPayMethodApiModel, res as Object[], { strategy: 'excludeAll' })));
  }

  public removeExternalPaymentMethod(request: PaymentProviderPayMethodRequestModel): Observable<any> {
    return this.http.delete(`/cinema/${request.cinemaId}/order/${request.orderId}/payment/${request.paymentProvider}`, {
      params: {
        channel: request.paymentChannel
      }
    });
  }

  public getVirtualPass(
    cinemaId: string,
    salesDocumentId: string,
    virtualPassProvider: string,
    screeningId: string | undefined,
    mode: 'reservation' | 'ticket' | 'alltickets' | undefined
  ): Observable<VirtualPassApiModel[]> {
    const url = `/cinema/${cinemaId}/salesDocument/${salesDocumentId}/provider/${virtualPassProvider}`;

    let params = new HttpParams();
    if (mode) {
      params = params.set('mode', mode);
    }
    if (screeningId) {
      params = params.set('screeningId', screeningId);
    }

    return this.http.get<VirtualPassApiModel[]>(url, { params });
  }

  @Cacheable({ maxAge: 3600000 })
  public getCatering(cinemaId: string, orderId: string = null, screenGroupId: string = null): Observable<CateringApiModel> {
    let params = new HttpParams();

    if (screenGroupId !== null) {
      params = params.append('screenGroupId', screenGroupId);
    }

    const path = orderId ? `/cinema/${cinemaId}/order/${orderId}/fb` : `/cinema/${cinemaId}/fb`;

    return this.http.get<CateringApiModel>(path, { params: params });
  }

  @Cacheable({ maxAge: 3600000 })
  public getPickupTime(cinemaId: string): Observable<PickupTimeApiModel[]> {
    return this.http.get<PickupTimeApiModel[]>(`/cinema/${cinemaId}/fb/pickupTime`);
  }

  public patchPickupTime(pickupTimeId: string, orderId: string, cinemaId: string): Observable<boolean> {
    return this.http.patch<OrderApiModel>(`/cinema/${cinemaId}/order/${orderId}/fb/pickupTime/${pickupTimeId}`, null).pipe(
      tap(() => OrderHttpService.cacheBuster()),
      map(() => true)
    );
  }

  public putPickupTime(cinemaId: string, orderId: string, pickupTime: DateTime) {
    return this.http.put(`/cinema/${cinemaId}/order/${orderId}/fb/pickupTime`, {
      pickupTime: `${pickupTime.toFormat('yyyy-LL-dd')}\T${pickupTime.toFormat('HH:mm:ss')}`
    });
  }

  public removeItem(cinemaId: string, orderId: string, itemId: string): Observable<OrderApiModel> {
    return this.http.delete<OrderApiModel>(`/cinema/${cinemaId}/order/${orderId}/item/${itemId}`).pipe(
      tap((res) => OrderHttpService.cacheModify(res.id, res)),
      map((res) => plainToInstance(OrderApiModel, res as Object, { strategy: 'excludeAll' }))
    );
  }

  public putTable(cinemaId: string, orderId: string, tableId: string): Observable<TableApiModel> {
    return this.http.put<TableApiModel>(`/cinema/${cinemaId}/order/${orderId}/table/${tableId}`, null);
  }

  public patchOrderFbItemSeat(cinemaId: string, orderId: string, body: SeatRequestRequestModel[]) {
    return this.http.patch(`/cinema/${cinemaId}/order/${orderId}/fbitem/seat`, body);
  }

  @Cacheable({
    maxAge: 3600000,
    shouldCacheDecider: (response) => {
      return Array.isArray(response) && response !== null && response.length > 0;
    }
  })
  public getFbNutritionalInfo(cinemaId: string) {
    return this.http.get<NutritionalInfoModel[]>(`/cinema/${cinemaId}/fb/nutritionalinfo`);
  }

  @Cacheable({
    maxAge: 3600000,
    shouldCacheDecider: (response) => {
      return Array.isArray(response) && response !== null && response.length > 0;
    }
  })
  public getOrderFb(cinemaId: string, orderId: string = null, screenGroupId: string = null): Observable<CateringResponseModel> {
    let params = new HttpParams();
    //params = params.append('forceReload', 'true');

    if (screenGroupId !== null) {
      params = params.append('screenGroupId', screenGroupId);
    }

    const path = orderId ? `/cinema/${cinemaId}/order/${orderId}/fb` : `/cinema/${cinemaId}/fb`;

    return this.http.get<CateringResponseModel>(path, { params: params });
  }

  public getApplePaySession(cinemaId: string, orderId: string, paymentProviderIdentifier: string): Observable<any> {
    return this.http.get<any>(`/cinema/${cinemaId}/order/${orderId}/payment/${paymentProviderIdentifier}/ApplePaySession`);
  }

  public recalculate(cinemaId: string, orderId: string, operationType: RecalculateOperationType) {
    return this.http.put<OrderApiModel>(`/cinema/${cinemaId}/order/${orderId}/recalculate`, {
      operationType: operationType
    });
  }

  public putExtendLifetime(cinemaId: string, orderId: string, endDate: DateTime) {
    return this.http.put(`/cinema/${cinemaId}/order/${orderId}/extendLifetime`, {
      endDate: endDate.toFormat("yyyy-MM-dd'T'HH:mm:ss")
    });
  }

  public putInvoiceData(cinemaId: string, orderId: string, invoiceData: InvoiceDataRequestModel): Observable<void> {
    return this.http.put<void>(`/cinema/${cinemaId}/order/${orderId}/invoiceData`, invoiceData);
  }

  public getTransactionNumberByVoucherNumber(voucherNumber: string): Observable<TransactionNumberByVoucherNumber> {
    return this.http.get<TransactionNumberByVoucherNumber>(`/order/transactionNumberByVoucherNumber/${voucherNumber}`);
  }

  public static cacheModify(key: string, responseData: Object): void {
    OrderHttpService.cacheModifier$.next((data: any[]) => {
      const oldCacheRow = data.find((p) => p.parameters[1] === key);

      if (!oldCacheRow) {
        return;
      }

      Object.assign(oldCacheRow.response, {
        ...responseData
      });

      return data;
    });
  }

  public static cacheBuster(): void {
    OrderHttpService.cacheBuster$.next();
    OrderTicketHttpService.cacheBuster$.next();
  }
}
